import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CourseEmptyBlock } from '@/components/patient/course/CourseChrome';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import { useAuth } from '@/lib/auth';
import { useJournalEntries } from '@/hooks/useJournal';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { patientJournalWritePath } from '@/lib/journalWritePath';
import { planStepJournalPersist } from '@/utils/journalPersist';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const ICON = 1.75;

export function TreatmentCourseStepJournal({
  clinicId,
  patientId,
  appointmentId,
  courseId,
  stepId,
  defaultTitle,
  emptyWhen,
}: {
  clinicId: string;
  patientId: string;
  appointmentId: string | null;
  courseId?: string | null;
  stepId?: string | null;
  currentUserId?: string | null;
  compose?: boolean;
  onComposeChange?: (next: boolean) => void;
  emptyWhen?: string;
  defaultTitle?: string;
  stepTitle?: string;
  toolbarEnd?: unknown;
  correctionStartToken?: number;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canCreate = hasPermission(clinicId, 'journal', 'create');
  const canAmend = hasPermission(clinicId, 'journal', 'amend');
  const entriesQuery = useJournalEntries(clinicId, { patientId });
  const notes = useMemo(
    () =>
      (entriesQuery.data ?? []).filter(
        (entry) => appointmentId && entry.appointment_id === appointmentId && !entry.discarded_at,
      ),
    [appointmentId, entriesQuery.data],
  );
  const persistPlan = useMemo(
    () => (appointmentId ? planStepJournalPersist(notes, appointmentId) : { kind: 'create' as const }),
    [appointmentId, notes],
  );

  function openWrite(amend = false) {
    navigate(
      patientJournalWritePath(patientId, {
        appointmentId,
        courseId,
        stepId,
        entryId: persistPlan.kind === 'create' ? null : persistPlan.entryId,
        amend,
        source: 'step',
        from: `${location.pathname}${location.search}`,
      }),
    );
  }

  if (!appointmentId) {
    return (
      <CourseEmptyBlock
        icon={<FileText className="h-5 w-5" />}
        title={t('treatment_courses.workspace_journal_needs_appointment')}
      />
    );
  }

  if (entriesQuery.isLoading) {
    return <p className={cn(TYPE.small, 'text-ds-muted')}>{t('app.loading')}</p>;
  }

  if (persistPlan.kind === 'finalized') {
    const note = notes.find((entry) => entry.id === persistPlan.entryId) ?? notes[0];
    return (
      <div className="space-y-s4">
        {note ? (
          <>
            <p className={cn(TYPE.small, 'text-ds-muted')}>
              {note.finalized_at
                ? t('journal.finalized_by', {
                    date: formatCourseDateTime(note.finalized_at, i18n.language),
                    name: note.author_name ?? '',
                  })
                : t('journal.filter_status_finalized')}
            </p>
            <JournalFormattedView html={note.content_html} text={note.content_md} />
          </>
        ) : null}
        {canAmend ? (
          <Button type="button" variant="secondary" onClick={() => openWrite(true)}>
            <Pencil className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('journal.add_correction')}
          </Button>
        ) : null}
      </div>
    );
  }

  if (persistPlan.kind === 'update') {
    const draft = notes.find((entry) => entry.id === persistPlan.entryId);
    return (
      <div className="space-y-s4">
        <p className={cn(TYPE.body, 'font-medium')}>{draft?.title || defaultTitle}</p>
        {canCreate ? (
          <Button type="button" onClick={() => openWrite()}>
            <Pencil className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('treatment_courses.continue_journal')}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <CourseEmptyBlock
      icon={<FileText className="h-5 w-5" />}
      title={t('treatment_courses.journal_empty_title')}
      description={emptyWhen ?? t('journal.empty_hint')}
      action={
        canCreate ? (
          <Button type="button" onClick={() => openWrite()}>
            <Pencil className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('treatment_courses.write_note')}
          </Button>
        ) : undefined
      }
    />
  );
}
